import {
  ICellRendererComp,
  ICellRendererParams,
  ColDef,
} from 'ag-grid-community';

interface AutoWidthAdjustingCellRendererParams<TData = any, TValue = any>
  extends ICellRendererParams<TData, TValue> {
  renderer: ColDef['cellRenderer'];
}

export class AutoWidthAdjustingCellRenderer<TData = any, TValue = any>
  implements ICellRendererComp<TData>
{
  root = document.createElement('span');
  renderer: ICellRendererComp | undefined;
  resizeObserver: ResizeObserver | undefined;
  intervalId: ReturnType<typeof setInterval> | undefined;

  constructor() {
    return new Proxy(this, this);
  }

  /**
   * Fallback to wrapped renderer if property is not defined directly in this class.
   */
  get(target: this, prop: string) {
    return this[prop] || this.renderer?.[prop];
  }

  getGui() {
    return this.root;
  }

  init(params: AutoWidthAdjustingCellRendererParams<TData, TValue>) {
    const resizeContainer = document.createElement('span');
    resizeContainer.classList.add('resize-container');
    resizeContainer.style.width = 'min-content';

    if (this.isClassBasedRenderer(params.renderer)) {
      const renderer = new params.renderer();
      renderer.init?.(params);
      // Append DOM element to resize container
      resizeContainer.appendChild(renderer.getGui());
      this.renderer = renderer;
    } else {
      // Append DOM element to resize container
      const renderResult = params.renderer(params);
      if (typeof renderResult === 'string') {
        resizeContainer.innerHTML = renderResult;
      } else {
        resizeContainer.appendChild(renderResult);
      }
    }

    this.root.appendChild(resizeContainer);

    const handleResize = () => {
      const { width } = resizeContainer.getBoundingClientRect();
      this.root.style.width = `${width}px`;
    };

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? // eslint-disable-next-line compat/compat
          new ResizeObserver(handleResize)
        : null;

    resizeObserver?.observe(resizeContainer);

    // note: when testing in chrome, there were times when a resize event
    // wouldn't fire when expected while the container size did actually change.
    // i think the resize observer might not work correctly with `width:
    // min-content` (?).
    this.intervalId = setInterval(handleResize, 250);

    handleResize();
  }

  destroy() {
    this.renderer?.destroy?.();
    this.resizeObserver?.disconnect();
    clearInterval(this.intervalId);
  }

  refresh(params: ICellRendererParams<any, any>) {
    return this.renderer?.refresh?.(params) ?? true;
  }

  isClassBasedRenderer(
    renderer: AutoWidthAdjustingCellRendererParams['renderer']
  ) {
    return renderer.prototype?.getGui; // This is how AG Grid differentiats class VS function renderers https://www.ag-grid.com/javascript-data-grid/component-cell-renderer/#cell-renderer-function
  }
}
